/**
 * Droits de gestion des collaborateurs — SOURCE UNIQUE, lue par le serveur ET par l'écran.
 *
 * Avant (09/2026), la règle était recopiée handler par handler dans `src/api/account.ts`
 * (`profileType !== "ORG" && profileType !== "ADM"`), et l'écran portait ses propres
 * drapeaux (`canManageUsers`, `canManageLicences`…) qui ne disaient pas la même chose :
 * RH et GES voyaient le crayon, les licences et la désactivation, et recevaient un 403
 * à chaque clic ; le MNG créait des collaborateurs qu'il ne pouvait ni inviter, ni
 * modifier, ni doter d'une licence. Même piège que `canMngPilotChallenge` : une règle
 * recopiée finit toujours par être oubliée quelque part. Les handlers appellent
 * `authorizeCollaboratorAction` (`src/lib/collaborator-authz.ts`), qui s'appuie ici.
 *
 * Portée : ORG, RH, GES agissent sur tout le compte ; le MNG n'agit que sur son
 * SOUS-ARBRE, lui-même exclu (même règle que la création et `resolveManagedCollaborator`).
 */

/** Codes profil, dans l'ORDRE de `profileTypeEnum` (schema.ts) — TypeScript ordonne les unions
 *  de littéraux selon leur première rencontre ; un autre ordre ici réécrit les messages
 *  d'erreur de tout le dépôt et fait diverger `scripts/typecheck-baseline.txt`. */
type ProfileCode = "ADM" | "COO" | "ORG" | "BNF" | "MNG" | "RH" | "CSL" | "GES";

export type CollaboratorAction =
  /** Ouvrir la fiche. */
  | "view"
  /** Identité, poste, langues, niveau d'expérience. */
  | "edit"
  /** Changer le responsable (managerId). */
  | "manager"
  /** Changer le profil (ORG/MNG/RH/GES/BNF) — borné par `assignableProfiles`. */
  | "profile"
  /**
   * Attribuer / retirer une licence nominative, PRISE DANS LE POOL DU COMPTE.
   * N'inclut PAS l'achat (packs, abonnements) : celui-ci reste gardé par
   * `canBuyLicences` (`src/lib/encadrant.ts`, lue par `src/api/buy-pack.ts`) et `PACK_MANAGER_PROFILES`
   * (`src/api/licence-grants.ts`), sans le MNG — décision
   * Valérie du 18/09/2026 : le MNG affecte les licences disponibles, il n'en achète pas.
   */
  | "licences"
  /** Inviter, renvoyer le lien de connexion. */
  | "invite"
  /** Désactiver / réactiver. */
  | "status"
  /** Créer un collaborateur. */
  | "create";

const ALL: readonly CollaboratorAction[] = ["view", "edit", "manager", "profile", "licences", "invite", "status", "create"];

/**
 * Matrice validée par Valérie le 18/09/2026.
 * GES suit le tableau des profils de CLAUDE.md : licences + activation, pas l'édition des fiches.
 */
const RIGHTS: Partial<Record<ProfileCode, readonly CollaboratorAction[]>> = {
  ADM: ALL,
  ORG: ALL,
  RH: ALL,
  MNG: ALL,
  GES: ["view", "licences", "status"],
};

/** Profils qu'un acteur peut ATTRIBUER. Un RH ne fait jamais d'ORG ; un MNG reste entre BNF et MNG. */
const ASSIGNABLE: Partial<Record<ProfileCode, readonly ProfileCode[]>> = {
  ADM: ["ORG", "MNG", "RH", "GES", "BNF"],
  ORG: ["ORG", "MNG", "RH", "GES", "BNF"],
  RH: ["MNG", "RH", "GES", "BNF"],
  MNG: ["MNG", "BNF"],
};

/** Profils qui peuvent agir sur un ORG (hors consultation et licences) : on ne laisse pas un RH désactiver son organisateur. */
const ORG_GUARDIANS: readonly string[] = ["ADM", "ORG"];

/** Actions qui n'ont pas de sens sur soi-même (se désactiver, se rétrograder). */
const NOT_ON_SELF: readonly CollaboratorAction[] = ["status", "profile"];

export function collaboratorRights(profileType?: string | null): Set<CollaboratorAction> {
  return new Set(profileType ? RIGHTS[profileType as ProfileCode] ?? [] : []);
}

export function canCollaborator(profileType: string | null | undefined, action: CollaboratorAction): boolean {
  return collaboratorRights(profileType).has(action);
}

export function assignableProfiles(profileType?: string | null): readonly string[] {
  return profileType ? ASSIGNABLE[profileType as ProfileCode] ?? [] : [];
}

/**
 * Profils qu'on peut donner À LA CRÉATION d'un collaborateur (arbitrage 7.1 du 25/09/2026) : la
 * matrice, sauf pour le RH, qui ne crée que des bénéficiaires (« RH : BNF »). Changer ensuite le
 * profil reste borné par `assignableProfiles` (matrice du 18/09 : un RH, jamais vers ORG).
 */
const CREATABLE_OVERRIDE: Partial<Record<ProfileCode, readonly ProfileCode[]>> = {
  RH: ["BNF"],
  // Q1 du 29/09/2026 (Valérie) : l'ADM garde la création des profils internes (ADM, COO, CSL), comme
  // en service, TANT QU'aucun vrai écran dédié n'existe (contrôles + email de bienvenue). Seul un
  // appel direct l'utilise aujourd'hui : l'assistant ne propose que ORG / MNG / RH / BNF.
  ADM: ["ADM", "COO", "CSL", "ORG", "MNG", "RH", "GES", "BNF"],
};
export function creatableProfiles(profileType?: string | null): readonly string[] {
  return (profileType && CREATABLE_OVERRIDE[profileType as ProfileCode]) || assignableProfiles(profileType);
}

export interface CollaboratorActionCheck {
  actor: { id: string; profileType: string };
  target: { id: string; profileType: string };
  action: CollaboratorAction;
  /** Pour un MNG seulement : la cible est-elle dans son sous-arbre (lui-même exclu) ? */
  inMngSubtree?: boolean;
}

export type CollaboratorDenial = { status: 400 | 403; message: string };

/**
 * Règle complète, SANS accès base (le sous-arbre MNG est fourni par l'appelant).
 * Renvoie `null` si l'action est permise, sinon le refus à renvoyer tel quel.
 */
export function checkCollaboratorAction({ actor, target, action, inMngSubtree }: CollaboratorActionCheck): CollaboratorDenial | null {
  if (!canCollaborator(actor.profileType, action)) {
    return { status: 403, message: "Accès refusé." };
  }
  if (NOT_ON_SELF.includes(action) && actor.id === target.id) {
    return { status: 400, message: action === "status" ? "Vous ne pouvez pas vous désactiver." : "Vous ne pouvez pas modifier votre propre profil." };
  }
  if (actor.profileType === "MNG" && actor.id !== target.id && !inMngSubtree) {
    return { status: 403, message: "Vous ne pouvez gérer que les collaborateurs de votre équipe." };
  }
  if (actor.profileType === "MNG" && actor.id === target.id && action !== "view") {
    return { status: 403, message: "Vous ne pouvez gérer que les collaborateurs de votre équipe." };
  }
  if (target.profileType === "ORG" && action !== "view" && action !== "licences" && !ORG_GUARDIANS.includes(actor.profileType)) {
    return { status: 403, message: "Seul un organisateur peut modifier un organisateur." };
  }
  return null;
}
